import { Button, EmptyState, Skeleton, cn, useErrorToast } from '@h2hspark/ui';
import { Bookmark, Plus, ScanLine, Sparkles, Users } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQrCodeSource } from '../../member/use-qr-code-source';
import { useScannerAvailability } from '../../member/use-scanner-availability';
import { useMyMeetList } from '../attendee-preview/api';
import { fullName, initialsOf, roleAtCompany } from '../attendee-preview/constants';
import { PER_EVENT_LOCKED } from '../event-registrations/constants';
import { PerEventShell } from '../event-registrations/per-event-page';

import {
  useAnnotateConnection,
  useConnectSurface,
  useDismissNotice,
  useScan,
  useSetOpenToConnect,
} from './api';
import { CaptureDialog } from './capture-dialog';
import {
  ADD_A_NOTE_LABEL,
  AT_THE_EVENT_HEADING,
  AT_THE_EVENT_SUBHEAD,
  CONNECT_CODE_HEADING,
  CONNECT_CODE_SCAN_PROMPT,
  CONNECT_CODE_SUPPORTING_LINE,
  CONNECT_QR_ALTERNATIVE_TEXT,
  INITIAL_SCAN_STAGE,
  INSTALL_HELP_PATH,
  MEET_LIST_CONTROL_LABEL,
  NOTE_SAVE_FAILED,
  NOTICE_DISMISS_LABEL,
  NOTICE_OPEN_CONNECTION_LABEL,
  OPEN_TO_CONNECT_LABEL,
  OPEN_TO_CONNECT_OFF_LABEL,
  OPEN_TO_CONNECT_OFF_DESCRIPTION,
  OPEN_TO_CONNECT_ON_DESCRIPTION,
  PEOPLE_YOU_MET_AT_THIS_EVENT_HEADING,
  PEOPLE_YOU_MET_EMPTY_DESCRIPTION,
  PEOPLE_YOU_MET_EMPTY_TITLE,
  ROSTER_EMPTY_DESCRIPTION,
  ROSTER_EMPTY_TITLE,
  ROSTER_HEADING,
  ROSTER_REGION_ID,
  SUGGESTION_ACTION_LABEL,
  SUGGESTION_HEADING,
  SCANNER_NEEDS_A_PHONE_ACTION,
  SCANNER_NEEDS_A_PHONE_DESCRIPTION,
  SCANNER_NEEDS_A_PHONE_TITLE,
  SCAN_ACTION_LABEL,
  connectQrPayload,
  nextScanStage,
  peopleYouMetCountLabel,
  scanFailureStage,
  type ScanStage,
} from './constants';
import { NoteComposerDialog } from './note-composer-dialog';
import { ScannerDialog } from './scanner-dialog';
import type {
  ConnectSurfaceOpen,
  InEventRosterRow,
  InEventSuggestion,
  PersonYouMet,
  ScanNotice,
} from './types';

/**
 * **The Connect page** — `Web App.dc.html` §2.6, `showConnect`, lines 516–565
 * (`MEM-028`…`MEM-031`, `MEM-053` AC-1, `MEM-056` AC-2).
 *
 * ## Top to bottom, as the prototype draws it
 *
 * The back link and the event header card with *During · Connect* active, then a heading row —
 * *At the event*, its sentence, and a **People I want to meet** control at the right — then a
 * `300px 1fr` grid: the purple connect-code panel on the left, and *People you met at this event*
 * with its count pill over a 2-column grid of met-person cards on the right. One column on a phone,
 * the code first (`MEM-056` AC-2).
 *
 * ## Three things are here that §2.6 does not draw, and each is a criterion
 *
 *   - **The scanner** (`MEM-028` AC-5). The prototype's desktop page has no scan control at all —
 *     Scan lives on the account page in that file — and on a laptop that is correct and stays
 *     correct (`MEM-031` AC-1, `PLT-008` AC-4). On a **phone** the scanner is the whole feature, so
 *     the heading row carries it and so does the code panel, gated on the device rather than on a
 *     width (`canRunScanner`).
 *   - **Who else is here** (`MEM-030` AC-3, AC-4). The in-event roster postdates the prototype's
 *     Connect screen. It sits under the two columns: the code and the people you have met are the
 *     answer to *what do I do now*, and the roster is the answer to *who else is in the room*.
 *   - **The suggestion prompt** (`MEM-030` AC-8, `MSG-029`), above the roster, because it is a
 *     pointer into it.
 *
 * ## And one the prototype draws as decoration, wired up
 *
 * The purple panel's *Open to connect* chip is a static dot and two words in the design. `MEM-030`
 * AC-2 requires the status to be toggleable at any time, and the plan's rule for this handoff is
 * that dead controls are wired rather than left dead — so the chip is the toggle, in its own place,
 * and the old bordered switch row is gone.
 */
export function MemberConnectPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();

  return (
    <PerEventShell eventId={eventId} surface="connect" locked={PER_EVENT_LOCKED.connect}>
      {() => <ConnectBody eventId={eventId} />}
    </PerEventShell>
  );
}

function ConnectBody({ eventId }: { readonly eventId: string }): React.ReactElement {
  const query = useConnectSurface(eventId, { enabled: eventId.length > 0 });
  const meetQuery = useMyMeetList(eventId, { enabled: true });

  const scan = useScan(eventId);
  const annotate = useAnnotateConnection(eventId);
  const dismiss = useDismissNotice(eventId);
  const toggle = useSetOpenToConnect(eventId);
  const showError = useErrorToast();

  const [stage, setStage] = React.useState<ScanStage>(INITIAL_SCAN_STAGE);
  const [noteFor, setNoteFor] = React.useState<PersonYouMet | null>(null);
  const scannerIsUsable = useScannerAvailability();

  const surface = query.data?.surface ?? null;
  const payload = surface !== null && surface.state === 'open' ? connectQrPayload(surface) : null;
  const qrSource = useQrCodeSource(payload);

  React.useEffect(() => {
    if (annotate.error !== null) showError(annotate.error, NOTE_SAVE_FAILED);
  }, [annotate.error, showError]);

  if (query.isPending) return <ConnectSkeleton />;

  if (surface === null) {
    return (
      <EmptyState
        icon={<Users className="h-6 w-6" aria-hidden />}
        title="Connect is not available"
        description="We could not load this event's Connect area just now. Try again in a moment."
      />
    );
  }

  /*
    `MEM-031` AC-5: **an explanation, not a missing screen.** The sentence comes from the server so
    that what a member reads is what the server actually applied — including the case that matters,
    where the door scanned them between the render and the read.

    `MEM-031` AC-6: there is no control here. Not a disabled one, not a "let us know you're here" —
    the closed payload carries five fields and none of them is an action. The stepper above already
    says the same thing in a cell (`PHASE_NOTES`), which is the sentence and not a second door.
  */
  if (surface.state === 'closed') {
    return (
      <div className="space-y-5">
        <h2 className="text-2xl tracking-tight md:text-3xl">{AT_THE_EVENT_HEADING}</h2>
        <section className="max-w-[600px] rounded-lg border border-border-subtle bg-muted/50 p-4">
          <p className="text-sm leading-relaxed text-text-body">{surface.notice}</p>
        </section>
      </div>
    );
  }

  /** One tap. Nothing between this call and the record — see `api.ts` and `MEM-029` AC-1. */
  const submitCode = (token: string): void => {
    scan.mutate(token, {
      onSuccess: (outcome) => {
        setStage(nextScanStage({ kind: 'answered', outcome }));
      },
      // `MEM-031` AC-3: it fails visibly and nothing was written. `scanFailureStage` picks which
      // of the two true sentences to say — there is deliberately no branch here that treats a
      // failure as anything but a failure.
      onError: (error) => {
        setStage(scanFailureStage(error));
      },
    });
  };

  const meetCount = meetQuery.data?.meetList?.entries.length ?? 0;

  return (
    <div className="space-y-7">
      {/* §2.6, item 3's heading row. */}
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <h2 className="mb-1.5 text-2xl tracking-tight md:text-3xl">{AT_THE_EVENT_HEADING}</h2>
          <p className="max-w-[600px] text-md text-muted-foreground md:text-lg">
            {AT_THE_EVENT_SUBHEAD}
          </p>
        </div>
        <div className="flex flex-none flex-wrap items-center gap-2.5">
          {/* `MEM-028` AC-5, entry point one — on a phone only (`MEM-031` AC-1). */}
          {scannerIsUsable ? (
            <Button
              className="rounded-pill"
              onClick={() => {
                setStage({ kind: 'scanning' });
              }}
            >
              <ScanLine className="mr-1.5 h-4 w-4" aria-hidden />
              {SCAN_ACTION_LABEL}
            </Button>
          ) : null}
          {/* §2.6's `openMeetOnly`: the meet list, which lives on the Matches page's attendee
              grid. The count is `MEM-013`'s own list, not a fixture. */}
          <Link
            to={`/app/events/${encodeURIComponent(eventId)}/matches`}
            className="inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-pill border-[1.5px] border-primary bg-card px-[18px] py-3 font-brand text-sm font-bold text-primary"
          >
            <Bookmark className="h-4 w-4" aria-hidden />
            {MEET_LIST_CONTROL_LABEL}
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 text-[11px] font-black text-primary-foreground">
              {meetCount}
            </span>
          </Link>
        </div>
      </div>

      {/* `MEM-029` AC-5. Above everything, because it is about something that already happened. */}
      {surface.notices.map((notice) => (
        <NoticeRow
          key={notice.attendeeRef}
          notice={notice}
          eventId={eventId}
          busy={dismiss.isPending}
          onDismiss={() => {
            dismiss.mutate(notice.attendeeRef);
          }}
        />
      ))}

      {/*
        `MEM-031` AC-1 and AC-2 — **explained rather than approximated.** On a laptop this is a
        panel of prose and a link, not a greyed-out scanner: a disabled scanner implies the feature
        should work here and does not. The code beside it is still drawn, because a member on a
        laptop can have their code scanned by somebody else's phone, and that half works exactly as
        intended.
      */}
      {scannerIsUsable ? null : (
        <section className="max-w-[600px] space-y-2 rounded-lg border border-border-subtle bg-muted/50 p-4">
          <p className="font-brand text-sm font-bold text-foreground">
            {SCANNER_NEEDS_A_PHONE_TITLE}
          </p>
          <p className="text-sm leading-relaxed text-text-body">
            {SCANNER_NEEDS_A_PHONE_DESCRIPTION}
          </p>
          <Link
            to={INSTALL_HELP_PATH}
            className="inline-flex font-brand text-sm font-bold text-brand"
          >
            {SCANNER_NEEDS_A_PHONE_ACTION}
          </Link>
        </section>
      )}

      {/* §2.6: `grid-template-columns:300px 1fr; gap:28px; align-items:start`, one column below
          `lg` with the code first. `lg` rather than `md` for the event page's reason — at 768px a
          300px panel leaves 404px for a two-column card grid, which is one column with extra
          steps. */}
      <div className="grid grid-cols-1 gap-7 lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start">
        <ConnectCodeCard
          qrSource={qrSource}
          openToConnect={surface.openToConnect}
          busy={toggle.isPending}
          onToggleOpen={(next) => {
            toggle.mutate(next);
          }}
          scannerIsUsable={scannerIsUsable}
          onOpenScanner={() => {
            setStage({ kind: 'scanning' });
          }}
        />

        <PeopleYouMetPanel people={surface.peopleYouMet} onNote={setNoteFor} />
      </div>

      {/*
        `MEM-030` AC-8, `MSG-029`. Above the roster because it is a pointer *into* it: the member
        reads one name, then finds them on the list beneath. `null` draws nothing at all — a card
        saying there is nobody to suggest is not help, it is the absence with a border round it.
      */}
      <SuggestionPrompt suggestion={surface.suggestion} />

      <RosterPanel roster={surface.roster} />

      {/* A **profile** code, read here (`MEM-057` AC-7). The scanner has closed — there is nothing
          to capture against a connection with no event — so the outcome is said on the page, where
          the member is now looking. It is a status rather than a dialog because the sentence is the
          whole of it: the connection exists, and it is not part of this evening. */}
      {stage.kind === 'connected-elsewhere' ? (
        <p
          role="status"
          className="rounded-lg border border-border-subtle bg-muted/50 p-4 text-sm leading-relaxed text-text-body"
        >
          {stage.message}
        </p>
      ) : null}

      <ScannerDialog
        stage={stage}
        busy={scan.isPending}
        onSubmitCode={submitCode}
        onClose={() => {
          setStage(INITIAL_SCAN_STAGE);
        }}
      />

      {/* Rendered only for a captured stage, which is only reachable from a logged connection. */}
      <CaptureDialog
        stage={stage}
        busy={annotate.isPending}
        onSave={(input) => {
          annotate.mutate(input, {
            onSettled: () => {
              setStage(INITIAL_SCAN_STAGE);
            },
          });
        }}
        onSkip={() => {
          setStage(INITIAL_SCAN_STAGE);
        }}
      />

      <NoteComposerDialog
        person={noteFor}
        busy={annotate.isPending}
        onSave={(input) => {
          annotate.mutate(input, {
            onSettled: () => {
              setNoteFor(null);
            },
          });
        }}
        onClose={() => {
          setNoteFor(null);
        }}
      />
    </div>
  );
}

/**
 * `MEM-028` AC-3 — the connect code, large, on a high-contrast card (§2.6's purple panel).
 *
 * The card is `--purple-800` and the code sits on a white tile inside it. That split is a scanning
 * requirement rather than a style: a QR needs a light quiet zone on all four sides to be found at
 * all, and the tile is what guarantees one whatever the card behind it becomes.
 *
 * The whole card is the second scanner entry point (`MEM-028` AC-5) on a device that can scan —
 * and on one that cannot it is a section rather than a button, because an affordance that opens
 * nothing is the disabled scanner `MEM-031` AC-2 refuses, one step quieter.
 *
 * **The status chip is the toggle** (`MEM-030` AC-1, AC-2). Its description changes with the state
 * and neither wording implies a lock: the status is advisory, so "off" means the member has left a
 * list, not that they have closed a door.
 */
function ConnectCodeCard({
  qrSource,
  openToConnect,
  busy,
  onToggleOpen,
  scannerIsUsable,
  onOpenScanner,
}: {
  readonly qrSource: string | null;
  readonly openToConnect: boolean;
  readonly busy: boolean;
  readonly onToggleOpen: (next: boolean) => void;
  readonly scannerIsUsable: boolean;
  readonly onOpenScanner: () => void;
}): React.ReactElement {
  const code = (
    <>
      <p className="font-brand text-md font-bold">{CONNECT_CODE_HEADING}</p>
      <p className="mt-1 text-xs opacity-85">{CONNECT_CODE_SUPPORTING_LINE}</p>
      <div className="mx-auto mt-[18px] w-[200px] max-w-full rounded-md bg-white p-3.5 shadow-md">
        {qrSource === null ? (
          <Skeleton className="aspect-square w-full rounded" />
        ) : (
          <img src={qrSource} alt={CONNECT_QR_ALTERNATIVE_TEXT} className="h-auto w-full" />
        )}
      </div>
      {scannerIsUsable ? (
        <span className="mt-3.5 inline-flex items-center gap-1.5 font-brand text-xs font-bold opacity-90">
          <ScanLine className="h-3.5 w-3.5" aria-hidden />
          {CONNECT_CODE_SCAN_PROMPT}
        </span>
      ) : null}
    </>
  );

  return (
    <section className="rounded-xl bg-brand-800 p-6 text-center text-white shadow-md md:p-[26px]">
      {scannerIsUsable ? (
        <button type="button" onClick={onOpenScanner} className="w-full">
          {code}
        </button>
      ) : (
        code
      )}

      {/* §2.6's chip: `rgba(255,255,255,.14)`, a 9px dot, two words in brand 700. A real control. */}
      <button
        type="button"
        role="switch"
        aria-checked={openToConnect}
        disabled={busy}
        onClick={() => {
          onToggleOpen(!openToConnect);
        }}
        /* **A focus ring in the panel's own colours.** This control carried no focus treatment at
           all, so it fell back to the user agent's ring on `--purple-800` — visible, but not the
           design's, and not something the repository's focus guard can see: that test only fires
           where an outline was *removed*. White at `ring-offset-brand-800`, because
           `ring-offset-background` is the page's ivory and would draw a pale gap through the
           panel. Parity pass, 2026-09-12 (`PLT-007` AC-3). */
        className="mt-[18px] inline-flex items-center justify-center gap-2 rounded-pill bg-white/15 px-3.5 py-2.5 font-brand text-xs font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-800 disabled:opacity-60"
      >
        <span
          className={cn(
            'h-[9px] w-[9px] rounded-pill',
            openToConnect ? 'bg-success' : 'bg-white/45',
          )}
          aria-hidden
        />
        {openToConnect ? OPEN_TO_CONNECT_LABEL : OPEN_TO_CONNECT_OFF_LABEL}
      </button>
      <p className="mx-auto mt-2 max-w-[240px] text-xs text-white/75">
        {openToConnect ? OPEN_TO_CONNECT_ON_DESCRIPTION : OPEN_TO_CONNECT_OFF_DESCRIPTION}
      </p>
    </section>
  );
}

/**
 * `MEM-030` AC-6 — *People you met at this event*, with the count badge, and §3.11's cards.
 *
 * The badge renders `people.length`. There is no separate count on the payload and there must not
 * be: two numbers for one list is how a badge and a list start disagreeing. (The prototype's is a
 * hard-coded `3`.)
 *
 * **The note is the member's own** and reaches nobody else, including its subject. Tapping it, or
 * *Add a note* where there is none, opens the composer.
 */
function PeopleYouMetPanel({
  people,
  onNote,
}: {
  readonly people: ReadonlyArray<PersonYouMet>;
  readonly onNote: (person: PersonYouMet) => void;
}): React.ReactElement {
  return (
    <section className="min-w-0">
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg md:text-xl">{PEOPLE_YOU_MET_AT_THIS_EVENT_HEADING}</h3>
        <span
          aria-label={peopleYouMetCountLabel(people.length)}
          className="rounded-pill bg-fuchsia-100 px-[11px] py-[5px] font-brand text-2xs font-bold text-fuchsia-700"
        >
          {people.length}
        </span>
      </div>
      {people.length === 0 ? (
        <EmptyState
          icon={<ScanLine className="h-6 w-6" aria-hidden />}
          title={PEOPLE_YOU_MET_EMPTY_TITLE}
          description={PEOPLE_YOU_MET_EMPTY_DESCRIPTION}
        />
      ) : (
        /* §2.6: `repeat(2, minmax(0,1fr)); gap:12px` inside the right column. */
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {people.map((person) => (
            <li
              key={person.attendeeRef}
              className="flex min-w-0 items-start gap-3 rounded-lg border border-border-subtle bg-card p-3 shadow-xs md:gap-[13px] md:p-4"
            >
              <PersonAvatar person={person} size="met" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-brand text-sm font-bold text-foreground md:text-md">
                  {fullName(person)}
                </p>
                {roleAtCompany(person) === null ? null : (
                  <p className="truncate text-xs text-muted-foreground md:text-sm">
                    {roleAtCompany(person)}
                  </p>
                )}
                {/* §3.11: the note in italics, or *Add a note* in brand. The prototype's intent
                    badge — "Follow up" / "Maybe" — is the member's own `followUpIntent`, and it is
                    drawn only when they actually said (`null` is "has not said", not "no"). */}
                {person.note === null ? (
                  <button
                    type="button"
                    onClick={() => {
                      onNote(person);
                    }}
                    className="mt-[7px] inline-flex items-center gap-1.5 font-brand text-sm font-semibold text-primary"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                    {ADD_A_NOTE_LABEL}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      onNote(person);
                    }}
                    className="mt-[7px] block text-left text-sm italic text-text-body"
                  >
                    {person.note}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * `MEM-030` AC-8 — the in-event suggestion prompt (`MSG-029`).
 *
 * **The sentence is served and this component composes nothing.** `message` names the person and
 * says why, in one string, for the same reason `ScanNotice` carries its name inside `message`: one
 * place a person is rendered, so there cannot be a second that drifts.
 *
 * The control scrolls to the roster rather than opening a profile. The prompt's whole job is
 * *"that person is standing over there"*, and the roster row beneath already carries the
 * conversation starter a member would open a profile for.
 */
function SuggestionPrompt({
  suggestion,
}: {
  readonly suggestion: InEventSuggestion | null;
}): React.ReactElement | null {
  if (suggestion === null) return null;
  return (
    <section
      data-testid="in-event-suggestion"
      data-reason={suggestion.reason}
      className="max-w-[600px] space-y-2 rounded-lg border border-brand-200 bg-brand-50 p-4"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-600" aria-hidden />
        <h2 className="font-brand text-sm font-bold text-brand-600">{SUGGESTION_HEADING}</h2>
      </div>
      <p className="text-sm leading-relaxed text-text-body">{suggestion.message}</p>
      <a
        href={`#${ROSTER_REGION_ID}`}
        className="inline-flex font-brand text-sm font-bold text-brand"
      >
        {SUGGESTION_ACTION_LABEL}
      </a>
    </section>
  );
}

/**
 * `MEM-030` AC-3 — who else is here.
 *
 * Each row draws the six fields a preview card carries and no more; there is no Connect action on a
 * row here, because connecting from a roster is a **request** (`MEM-032`) and the Matches page's
 * attendee grid is where that control lives. An action here that *logged* a connection would be the
 * in-room consent model applied to somebody across the room who is not holding their code out.
 */
function RosterPanel({
  roster,
}: {
  readonly roster: ReadonlyArray<InEventRosterRow>;
}): React.ReactElement {
  return (
    <section id={ROSTER_REGION_ID} className="space-y-3.5">
      <h2 className="text-lg md:text-xl">{ROSTER_HEADING}</h2>
      {roster.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" aria-hidden />}
          title={ROSTER_EMPTY_TITLE}
          description={ROSTER_EMPTY_DESCRIPTION}
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {roster.map((person) => (
            <li
              key={person.attendeeRef}
              className="flex min-w-0 gap-3 rounded-lg border border-border-subtle bg-card p-3 shadow-xs"
            >
              <PersonAvatar person={person} size="roster" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-brand text-sm font-bold text-foreground">
                  {fullName(person)}
                </p>
                {roleAtCompany(person) === null ? null : (
                  <p className="truncate text-xs text-muted-foreground">{roleAtCompany(person)}</p>
                )}
                {person.conversationStarter === null ? null : (
                  <p className="mt-1.5 text-xs leading-snug text-text-body">
                    {person.conversationStarter}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * `MEM-029` AC-5 — the scanned member's notice.
 *
 * **One sentence, one link, one dismissal.** The sentence comes from the server verbatim; this
 * component composes nothing, which is what stops the disclosure widening by a well-meaning edit.
 * The link is `MEM-EVT-008`'s *"control opening the new connection"*.
 */
function NoticeRow({
  notice,
  eventId,
  busy,
  onDismiss,
}: {
  readonly notice: ScanNotice;
  readonly eventId: string;
  readonly busy: boolean;
  readonly onDismiss: () => void;
}): React.ReactElement {
  return (
    <section
      role="status"
      className="max-w-[600px] space-y-2 rounded-lg border border-brand/25 bg-brand-50 p-3.5"
    >
      <p className="text-sm text-text-body">{notice.message}</p>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" asChild>
          <Link
            to={`/app/events/${encodeURIComponent(eventId)}/attendees/${encodeURIComponent(
              notice.attendeeRef,
            )}`}
          >
            {NOTICE_OPEN_CONNECTION_LABEL}
          </Link>
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDismiss}>
          {NOTICE_DISMISS_LABEL}
        </Button>
      </div>
    </section>
  );
}

/**
 * A person's initials disc, as §3.25 draws every one of them: a **circle** in `--purple-100` with
 * `--purple-700` letters, at `font-brand` 700.
 *
 * Both lists on this page were drawing people with `InitialsChip`, whose own header says it is
 * the **organization** mark — a 9px-cornered square in `--brand-50` — so the two person lists here
 * disagreed with the person rows on every other screen (the roster's mini-row, the attendee card,
 * the match card, the connection card) and with the design, which draws an organization square and
 * a person round on purpose. Found in the parity pass, 2026-09-12.
 */
function PersonAvatar({
  person,
  size,
}: {
  readonly person: { readonly firstName: string; readonly lastName: string };
  /** §3.11's met-person card is 46px; the in-room roster is the denser 40px of a list row. */
  readonly size: 'met' | 'roster';
}): React.ReactElement {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex flex-none items-center justify-center rounded-pill bg-brand-100 font-brand font-bold text-brand-700',
        size === 'met' ? 'h-11 w-11 text-[15px] md:h-[46px] md:w-[46px]' : 'h-10 w-10 text-sm',
      )}
    >
      {initialsOf(person)}
    </span>
  );
}

function ConnectSkeleton(): React.ReactElement {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-2/3" />
      <div className="grid grid-cols-1 gap-7 lg:grid-cols-[300px_minmax(0,1fr)]">
        <Skeleton className="h-[360px] w-full rounded-xl" />
        <Skeleton className="h-[200px] w-full rounded-xl" />
      </div>
    </div>
  );
}

/** Re-exported so the route file names one thing. */
export type { ConnectSurfaceOpen };
